import { useCallback, useEffect, useMemo, useState } from "react";
import { CapybaraAvatar } from "./components/CapybaraAvatar";
import { ReactionTracker } from "./components/ReactionTracker";
import { ChatPanel } from "./components/ChatPanel";
import { AudioToggle } from "./components/AudioToggle";
import { useQuantyLLM } from "./hooks/useQuantyLLM";
import { useIdleTimer } from "./hooks/useIdleTimer";
import { playReactionAudio, playCrunchLayer } from "./lib/audio";
import { detectDisgust, detectFinance, detectQuantum } from "./lib/accessories";
import {
  loadHistory,
  loadSeenEmotions,
  persistHistory,
  persistSeenEmotions,
  clearStorage,
} from "./lib/storage";
import type { ReactionId } from "./lib/emotionCatalog";
import type { AccessoryState, ConversationMessage } from "./types";
import "./App.css";

const INITIAL_ACCESSORIES: AccessoryState = {
  finance: false,
  quantum: false,
  disgust: false,
};

const PISTACHIO_REGEX = /pistachio/i;

export function App() {
  const [messages, setMessages] = useState<ConversationMessage[]>(() =>
    loadHistory()
  );
  const [input, setInput] = useState("");
  const [seenEmotions, setSeenEmotions] = useState<ReactionId[]>(() =>
    loadSeenEmotions()
  );
  const [currentReaction, setCurrentReaction] = useState<ReactionId | null>(
    null
  );
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [audioGateAccepted, setAudioGateAccepted] = useState(false);
  const [accessories, setAccessories] =
    useState<AccessoryState>(INITIAL_ACCESSORIES);
  const [sending, setSending] = useState(false);
  const { idleAnimation, registerActivity } = useIdleTimer(sending);
  const { ready, status, error, sendPrompt } = useQuantyLLM();

  useEffect(() => {
    persistHistory(messages);
  }, [messages]);

  useEffect(() => {
    persistSeenEmotions(seenEmotions);
  }, [seenEmotions]);

  useEffect(() => {
    async function requestPersistence() {
      if (!navigator.storage?.persist) {
        return;
      }
      const already = (await navigator.storage.persisted?.()) ?? false;
      if (!already) {
        await navigator.storage.persist();
      }
    }
    requestPersistence().catch((err) =>
      console.warn("Storage persist failed", err)
    );
  }, []);

  const conversationForLLM = useMemo(
    () =>
      messages.map((msg) => ({
        role: msg.role,
        content: msg.llmContent ?? msg.text,
      })),
    [messages]
  );

  const onInputChange = useCallback((value: string) => {
    setInput(value);
    setAccessories((prev) => ({
      ...prev,
      finance: detectFinance(value) || prev.finance,
      quantum: detectQuantum(value) || prev.quantum,
      disgust: detectDisgust(value) || prev.disgust,
    }));
  }, []);

  const updateAccessoryState = useCallback((text: string) => {
    setAccessories({
      finance: detectFinance(text),
      quantum: detectQuantum(text),
      disgust: detectDisgust(text),
    });
  }, []);

  const handleSend = useCallback(async () => {
    if (!ready || sending || !input.trim()) {
      return;
    }
    const userText = input.trim();
    setInput("");
    registerActivity();
    updateAccessoryState(userText);

    const userMessage: ConversationMessage = {
      id: crypto.randomUUID(),
      role: "user",
      text: userText,
      timestamp: Date.now(),
      llmContent: userText,
    };

    setMessages((prev) => [...prev, userMessage]);
    setSending(true);

    if (audioEnabled && PISTACHIO_REGEX.test(userText)) {
      void playReactionAudio("happy_laugh");
      setTimeout(() => {
        void playCrunchLayer();
      }, 350);
    }

    try {
      const llmResult = await sendPrompt(conversationForLLM, userText);
      const assistantMessage: ConversationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        text: describeReaction(llmResult.reaction),
        timestamp: Date.now(),
        reaction: llmResult.reaction,
        llmContent: llmResult.raw,
        thinking: llmResult.thinking,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setCurrentReaction(llmResult.reaction);
      if (!seenEmotions.includes(llmResult.reaction)) {
        setSeenEmotions((prev) => [...prev, llmResult.reaction]);
      }
      if (audioEnabled) {
        await playReactionAudio(llmResult.reaction);
      }
    } catch (err) {
      console.error(err);
      const assistantMessage: ConversationMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        text: "Quanty couldn't react. Please try again.",
        timestamp: Date.now(),
        reaction: "confused",
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } finally {
      setSending(false);
    }
  }, [
    audioEnabled,
    conversationForLLM,
    input,
    ready,
    registerActivity,
    seenEmotions,
    sendPrompt,
    sending,
    updateAccessoryState,
  ]);

  const handleReset = useCallback(() => {
    setMessages([]);
    setSeenEmotions([]);
    setCurrentReaction(null);
    setAccessories(INITIAL_ACCESSORIES);
    clearStorage();
  }, []);

  const primaryStatus = error ? `Error: ${error}` : status;

  const displayedMessages = useMemo(() => messages.slice(-1), [messages]);

  const unlockAudio = useCallback(() => {
    setAudioGateAccepted(true);
    setAudioEnabled(true);
  }, []);

  const canUnlockAudio = ready && !sending;

  return (
    <main className="app-shell">
      {false && <div className="hero">
        <div className="hero-top">
          <h1>Quanty</h1>
          <p>Offline capybara companion</p>
        </div>
        <AudioToggle
          enabled={audioEnabled}
          onToggle={() => setAudioEnabled((prev) => !prev)}
        />
      </div>}

      <div className="stack">
        <section className="panel avatar-card">
          <CapybaraAvatar
            reaction={currentReaction}
            accessories={accessories}
            idleAnimation={idleAnimation}
            isThinking={sending}
          />
        </section>

        <ChatPanel
          messages={displayedMessages}
          input={input}
          onInputChange={onInputChange}
          onSubmit={handleSend}
          disabled={!ready || sending}
          status={primaryStatus}
          ready={ready}
          onReset={handleReset}
        />
        <section className="panel">
          <ul className="status-stack">
            <li>{seenEmotions.length} reactions discovered</li>
          </ul>
        </section>
        <ReactionTracker seen={seenEmotions} />
      </div>

      {!audioGateAccepted && (
        <div className="audio-gate" role="dialog" aria-modal="true">
          <div className="audio-gate__content">
            <h2>Unmute Quanty</h2>
            <p>Tap to allow sound. The experience needs audio.</p>
            <p className="audio-gate__status">Status: {primaryStatus}</p>
            <button
              type="button"
              className="audio-gate__button"
              onClick={unlockAudio}
              disabled={!canUnlockAudio}
            >
              {canUnlockAudio ? "Unmute audio" : "Loading Quanty..."}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

function describeReaction(reaction: ReactionId): string {
  return reaction;
}
